import React, { useRef, useState } from 'react'

import { faGlobe } from '@fortawesome/pro-solid-svg-icons/faGlobe'
import { Box, Container, styled, Typography } from '@mui/material'

import { Button } from '@/components/Button/Button'
import { Icon } from '@/components/Icon/Icon'
import { pxToRem } from '@/shared/styles/themes'
import { multiLingualShowcaseData } from '@/slices/HomePage/mocks/multiLingualShowcase/data'

export const MultiLingualShowcase: React.FC = () => {
  const {
    eyebrow,
    headline,
    headlineAccent,
    subtitle,
    sampleTitleLabel,
    sampleTitle,
    pickerHeading,
    ctaText,
    ctaLink,
    footnote,
    languages,
  } = multiLingualShowcaseData

  const [selectedCode, setSelectedCode] = useState<string>(languages[0]?.code ?? 'en')
  const selected = languages.find((l) => l.code === selectedCode) ?? languages[0]
  const chipRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  const handleSelect = (code: string) => {
    setSelectedCode(code)
    chipRefs.current[code]?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    })
  }

  return (
    <StyledSection>
      <Container maxWidth="lg">
        <StyledHeader>
          <StyledEyebrow>
            <Icon icon={faGlobe} sx={{ fontSize: pxToRem(14), mr: 1 }} />
            {eyebrow}
          </StyledEyebrow>
          <StyledHeadline variant="h2">
            {headline} <StyledAccent>{headlineAccent}</StyledAccent>
          </StyledHeadline>
          <StyledSubtitle variant="body1">{subtitle}</StyledSubtitle>
        </StyledHeader>

        <StyledBookStage>
          <StyledBook key={selected.code} lang={selected.code}>
            <StyledBookSpine />
            <StyledBookHeader>
              <StyledBookLanguageTag>
                <span aria-hidden>{selected.flagEmoji}</span>
                <span>{selected.nameNative}</span>
                <StyledBookLanguageMuted>
                  {selected.nameEnglish}
                </StyledBookLanguageMuted>
              </StyledBookLanguageTag>
              <StyledBookSampleLabel>{sampleTitleLabel}</StyledBookSampleLabel>
            </StyledBookHeader>
            <StyledBookTitle>{sampleTitle}</StyledBookTitle>
            <StyledBookBody>“{selected.sampleSentence}”</StyledBookBody>
            <StyledBookFooter>
              <StyledPageNumber>1</StyledPageNumber>
            </StyledBookFooter>
          </StyledBook>
        </StyledBookStage>

        <StyledPickerHeading variant="overline">
          {pickerHeading}
        </StyledPickerHeading>

        <StyledChipScroller role="tablist" aria-label="Language previews">
          <StyledChipGrid>
            {languages.map((lang) => {
              const isSelected = lang.code === selected.code

              return (
                <StyledChip
                  key={lang.code}
                  ref={(el) => {
                    chipRefs.current[lang.code] = el
                  }}
                  type="button"
                  role="tab"
                  onClick={() => handleSelect(lang.code)}
                  aria-pressed={isSelected}
                  aria-selected={isSelected}
                  aria-label={`Preview ${lang.nameEnglish} opening line`}
                  $selected={isSelected}
                >
                  <StyledChipFlag aria-hidden>{lang.flagEmoji}</StyledChipFlag>
                  <StyledChipLabelGroup>
                    <StyledChipNative $selected={isSelected}>
                      {lang.nameNative}
                    </StyledChipNative>
                    <StyledChipEnglish>{lang.nameEnglish}</StyledChipEnglish>
                  </StyledChipLabelGroup>
                </StyledChip>
              )
            })}
          </StyledChipGrid>
          <StyledScrollFadeLeft aria-hidden />
          <StyledScrollFadeRight aria-hidden />
        </StyledChipScroller>

        <StyledCtaRow>
          <StyledCtaButton arrow primary size="large" href={ctaLink}>
            {ctaText}
          </StyledCtaButton>
          <StyledFootnote>{footnote}</StyledFootnote>
        </StyledCtaRow>
      </Container>
    </StyledSection>
  )
}

const StyledSection = styled('section')(({ theme }) => ({
  background: 'linear-gradient(160deg, #EEF3FB 0%, #F9F1FF 55%, #FFF3E8 100%)',
  padding: theme.spacing(5, 0),

  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(8, 0),
  },
}))

const StyledHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(4),

  [theme.breakpoints.up('md')]: {
    marginBottom: theme.spacing(5),
  },
}))

const StyledEyebrow = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: 'rgba(94, 75, 158, 0.14)',
  color: theme.palette.primary.main,
  padding: theme.spacing(0.75, 1.75),
  borderRadius: pxToRem(999),
  fontSize: pxToRem(13),
  fontWeight: 700,
  letterSpacing: pxToRem(1.2),
  textTransform: 'uppercase',
}))

const StyledHeadline = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 700,
  fontSize: pxToRem(28),
  lineHeight: 1.2,
  maxWidth: pxToRem(760),

  [theme.breakpoints.up('sm')]: {
    fontSize: pxToRem(34),
  },

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(40),
  },
}))

const StyledAccent = styled('span')(({ theme }) => ({
  color: theme.palette.primary.main,
  fontStyle: 'italic',
}))

const StyledSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  opacity: 0.8,
  maxWidth: pxToRem(640),
  fontSize: pxToRem(15),
  lineHeight: 1.55,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(17),
  },
}))

const StyledBookStage = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(2, 0),
}))

const StyledBook = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: pxToRem(620),
  minHeight: pxToRem(260),
  padding: theme.spacing(4, 3.5, 3.5, 5),
  background: 'linear-gradient(180deg, #FFFDF8 0%, #FFF6EA 100%)',
  borderRadius: pxToRem(14),
  boxShadow:
    '0 30px 60px -28px rgba(60, 40, 100, 0.35), 0 8px 20px rgba(60, 40, 100, 0.08)',
  overflow: 'hidden',
  animation: 'pixie-book-fade 320ms ease-out',

  '@keyframes pixie-book-fade': {
    from: { opacity: 0.35, transform: 'translateY(6px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },

  [theme.breakpoints.up('md')]: {
    minHeight: pxToRem(300),
    padding: theme.spacing(5, 5, 4, 6),
  },
}))

const StyledBookSpine = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  width: pxToRem(14),
  background:
    'linear-gradient(180deg, rgba(94, 75, 158, 0.35) 0%, rgba(94, 75, 158, 0.12) 100%)',
  borderRight: `1px solid ${theme.palette.primary.main}22`,

  [theme.breakpoints.up('md')]: {
    width: pxToRem(20),
  },
}))

const StyledBookHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  marginBottom: theme.spacing(2),
}))

const StyledBookLanguageTag = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1.25),
  borderRadius: pxToRem(999),
  backgroundColor: 'rgba(94, 75, 158, 0.08)',
  color: theme.palette.primary.main,
  fontWeight: 700,
  fontSize: pxToRem(13),

  '& > span:first-of-type': {
    fontSize: pxToRem(18),
    lineHeight: 1,
  },
}))

const StyledBookLanguageMuted = styled('span')(({ theme }) => ({
  color: theme.palette.primary.main,
  opacity: 0.55,
  fontWeight: 500,
  fontSize: pxToRem(12),
}))

const StyledBookSampleLabel = styled('span')(({ theme }) => ({
  fontSize: pxToRem(11),
  fontWeight: 700,
  letterSpacing: pxToRem(1.2),
  textTransform: 'uppercase',
  color: theme.palette.primary.main,
  opacity: 0.55,
}))

const StyledBookTitle = styled('h3')(({ theme }) => ({
  margin: 0,
  color: theme.palette.primary.main,
  fontWeight: 700,
  fontSize: pxToRem(20),
  marginBottom: theme.spacing(2),

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(24),
  },
}))

const StyledBookBody = styled('p')(({ theme }) => ({
  margin: 0,
  color: theme.palette.primary.main,
  fontSize: pxToRem(18),
  lineHeight: 1.55,
  fontStyle: 'italic',

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(22),
    lineHeight: 1.6,
  },
}))

const StyledBookFooter = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: theme.spacing(3),
}))

const StyledPageNumber = styled('span')(({ theme }) => ({
  fontSize: pxToRem(12),
  fontWeight: 600,
  letterSpacing: pxToRem(1),
  color: theme.palette.primary.main,
  opacity: 0.45,
}))

const StyledPickerHeading = styled(Typography)(({ theme }) => ({
  display: 'block',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2),
  textAlign: 'center',
  color: theme.palette.primary.main,
  opacity: 0.7,
  fontWeight: 700,
  letterSpacing: pxToRem(1.5),
}))

const StyledChipScroller = styled('div')(({ theme }) => ({
  position: 'relative',
  maxWidth: pxToRem(920),
  margin: '0 auto',
  marginLeft: theme.spacing(-2),
  marginRight: theme.spacing(-2),

  [theme.breakpoints.up('md')]: {
    marginLeft: 'auto',
    marginRight: 'auto',
  },
}))

const StyledChipGrid = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  gap: theme.spacing(1),
  overflowX: 'auto',
  scrollSnapType: 'x mandatory',
  WebkitOverflowScrolling: 'touch',
  padding: theme.spacing(0.5, 2.5),
  scrollPaddingInline: theme.spacing(2.5),

  // Hide scrollbar for a cleaner pill-strip look on mobile and desktop
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },

  [theme.breakpoints.up('md')]: {
    gap: theme.spacing(1.25),
    padding: theme.spacing(0.5, 4),
    scrollPaddingInline: theme.spacing(4),
    justifyContent: 'flex-start',
  },
}))

const StyledScrollFadeBase = styled('div')({
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: pxToRem(28),
  pointerEvents: 'none',
  zIndex: 1,
})

const StyledScrollFadeLeft = styled(StyledScrollFadeBase)(({ theme }) => ({
  left: 0,
  background: 'linear-gradient(90deg, rgba(246, 241, 252, 1) 0%, rgba(246, 241, 252, 0) 100%)',

  [theme.breakpoints.up('md')]: {
    width: pxToRem(48),
  },
}))

const StyledScrollFadeRight = styled(StyledScrollFadeBase)(({ theme }) => ({
  right: 0,
  background: 'linear-gradient(270deg, rgba(246, 241, 252, 1) 0%, rgba(246, 241, 252, 0) 100%)',

  [theme.breakpoints.up('md')]: {
    width: pxToRem(48),
  },
}))

const StyledChip = styled('button', {
  shouldForwardProp: (prop) => prop !== '$selected',
})<{ $selected: boolean }>(({ theme, $selected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 1.25),
  borderRadius: pxToRem(999),
  border: `1px solid ${$selected ? theme.palette.primary.main : 'rgba(94, 75, 158, 0.18)'}`,
  background: $selected ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)',
  color: theme.palette.primary.main,
  cursor: 'pointer',
  transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
  boxShadow: $selected
    ? '0 8px 18px rgba(94, 75, 158, 0.18)'
    : '0 2px 6px rgba(94, 75, 158, 0.06)',
  flexShrink: 0,
  scrollSnapAlign: 'center',

  '&:hover': {
    transform: 'translateY(-2px)',
    borderColor: theme.palette.primary.main,
  },

  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: pxToRem(3),
  },
}))

const StyledChipFlag = styled('span')({
  fontSize: pxToRem(20),
  lineHeight: 1,
  flexShrink: 0,
})

const StyledChipLabelGroup = styled('span')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  lineHeight: 1.1,
  minWidth: 0,
})

const StyledChipNative = styled('span', {
  shouldForwardProp: (prop) => prop !== '$selected',
})<{ $selected: boolean }>(({ $selected }) => ({
  fontSize: pxToRem(13),
  fontWeight: $selected ? 700 : 600,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
}))

const StyledChipEnglish = styled('span')(({ theme }) => ({
  fontSize: pxToRem(11),
  fontWeight: 500,
  color: theme.palette.primary.main,
  opacity: 0.55,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
}))

const StyledCtaRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(4),
}))

const StyledCtaButton = styled(Button)(({ theme }) => ({
  color: theme.palette.secondary.main,
  fontWeight: 600,
  padding: theme.spacing(2, 4),
}))

const StyledFootnote = styled('p')(({ theme }) => ({
  margin: 0,
  color: theme.palette.primary.main,
  opacity: 0.6,
  fontSize: pxToRem(13),
  textAlign: 'center',
  maxWidth: pxToRem(480),
}))
